import { useState, useEffect } from 'react'
import { callEdgeFunction } from '@/lib/edge-functions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { AlertCircle, CheckCircle, Loader2, Mail } from 'lucide-react'
import type { AdminRole } from '@/types/database'

interface RoleOption {
  value: AdminRole
  label: string
  description: string
}

interface InviteFormProps {
  availableRoles: RoleOption[]
  onSuccess?: () => void
  /** Reset form state when this key changes (e.g., dialog open state) */
  resetKey?: number
}

export function InviteForm({ availableRoles, onSuccess, resetKey }: InviteFormProps) {
  const [email, setEmail] = useState('')
  const [selectedRole, setSelectedRole] = useState<AdminRole>(
    availableRoles.length > 0 ? availableRoles[0].value : 'editor'
  )
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Reset form when resetKey changes (dialog reopens)
  useEffect(() => {
    setEmail('')
    setSelectedRole(
      availableRoles.length > 0 ? availableRoles[0].value : 'editor'
    )
    setError(null)
    setSuccess(false)
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  if (availableRoles.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          You don't have permission to invite team members.
        </AlertDescription>
      </Alert>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    setIsLoading(true)

    try {
      const result = await callEdgeFunction('admin-invite', {
        method: 'POST',
        body: { email, role: selectedRole, origin: window.location.origin },
        withAuth: true,
      })

      if (result.error) {
        setError(result.error || 'Failed to send invitation')
        return
      }

      setSuccess(true)
      setEmail('')

      // Call onSuccess after a brief delay so user sees the success message
      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500)
      }
    } catch {
      setError('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
          <CheckCircle className="h-6 w-6 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold">Invitation Sent!</h3>
        <p className="text-muted-foreground mt-1">
          We&apos;ve sent an invitation email with instructions to join the team.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">Email Address</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            placeholder="team@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            required
            className="pl-10"
          />
        </div>
      </div>

      <div className="space-y-3">
        <Label>Role</Label>
        <RadioGroup
          value={selectedRole}
          onValueChange={(value) => setSelectedRole(value as AdminRole)}
          disabled={isLoading}
        >
          {availableRoles.map((role) => (
            <div
              key={role.value}
              className="flex items-start space-x-3 rounded-lg border p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => setSelectedRole(role.value)}
            >
              <RadioGroupItem value={role.value} id={role.value} className="mt-1" />
              <div className="flex-1">
                <Label htmlFor={role.value} className="font-medium cursor-pointer">
                  {role.label}
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {role.description}
                </p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>

      <Button type="submit" disabled={isLoading} className="w-full">
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending Invitation...
          </>
        ) : (
          'Send Invitation'
        )}
      </Button>
    </form>
  )
}
